import { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { RESET, USER_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import {
  RANGE_SENSITIVITY_REFERENCE_TITLE,
  RANGE_SENSITIVITY_REFERENCE_TYPE,
} from 'common/constants/documents';
import { RANGE_SENSITIVITY_PAGE_KEY, RANGE_SENSITIVITY_PAGE_VALUE } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import CustomScalarSpreadsheet from 'components/CustomScalarSpreadsheet/CustomScalarSpreadsheet';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { valuationTabId } from 'pages/ValuationsAllocation/common/utils/valuationTabId';
import { useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import { useDocuments, useLocalStorage } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { getArrayValue, getStringValue } from 'utilities';
import { getRangeSensitivityRowGroupsInitialState } from './config/customRowConfig';
import { getRangeSensitivityPctWorkbookKey, RANGE_SENSITIVITY_SPREADSHEET_NAME } from './constants';
import { RangeSensitivityProps } from './types';
import { File as ValuationFile } from '../../types';
import ValuationContext from '../../ValuationContext';

const RangeSensitivity = ({
  spreadsheets,
  onChange,
  workbook,
  rangeSensitivity,
  spreadsheetWorkbookKey,
  allocation,
  securitiesCount,
  isDisabled,
  isLoading,
  isSaving,
}: RangeSensitivityProps) => {
  const {
    measurementDate,
    notesInRangeSensitivity,
    setNotesInRangeSensitivity,
    setFilesInRangeSensitivity,
    setSpreadsheetNotesInRangeSensitivity,
    registerApproachFormat,
  } = useContext(ValuationContext);
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, units: '' });
  const perShareFormat = useMemo(
    () => (format ? { ...format, currency: { ...format.currency, units: '' } } : format),
    [format]
  );
  const { isExportMode } = useLayoutContext();
  const featureId = rangeSensitivity.id ?? rangeSensitivity.panel_id;
  const spreadsheetNotesFeatureId = typeof featureId === 'number' ? featureId : undefined;
  const approachPanelId = getStringValue(rangeSensitivity.panel_id);
  const { filesToSave, setFilesToSave } = useDocuments();
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInRangeSensitivity,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInRangeSensitivity,
    setNotes,
    setNotesHasChanged,
  });
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    hasChanged: spreadsheetNotesHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();

  useEffect(() => {
    const tabId = valuationTabId('range_sensitivity', approachPanelId);
    registerApproachFormat?.(tabId, perShareFormat, formatDispatch);
  }, [approachPanelId, perShareFormat, formatDispatch, registerApproachFormat]);

  const rangeSensitivitySheet =
    spreadsheets[spreadsheetWorkbookKey as keyof typeof spreadsheets] ??
    spreadsheets[RANGE_SENSITIVITY_SPREADSHEET_NAME] ??
    Object.values(spreadsheets)[0];

  const pctWorkbookKey = getRangeSensitivityPctWorkbookKey(
    getStringValue(rangeSensitivity.panel_id)
  );
  const sensitivityPctSheet = spreadsheets[pctWorkbookKey as keyof typeof spreadsheets];

  const weightedShareValues = useMemo(
    () => getArrayValue(allocation?.weighted_share_values),
    [allocation?.weighted_share_values]
  );

  const rowGroupsInitialState = useMemo(
    () => getRangeSensitivityRowGroupsInitialState({ securitiesCount, weightedShareValues }),
    [securitiesCount, weightedShareValues]
  );

  // Persist the user's expand/collapse (hide) state per browser so it survives
  // reloads and, crucially, is readable by the separate same-origin PDF-export
  // iframe — which otherwise mounts fresh with everything expanded and reveals
  // rows the user had hidden. Keyed by the range-sensitivity feature id so each
  // panel keeps its own state.
  const rowGroupsStorageKey = featureId != null ? `range_sensitivity_row_groups_${featureId}` : '';
  const [persistedRowGroups, setPersistedRowGroups, , isRowGroupsHydrated] = useLocalStorage<RowsGroups | undefined>(
    rowGroupsStorageKey,
    undefined
  );

  // Merge persisted collapse overrides on top of the freshly built default state
  // (persisted wins), restricted to the aliases the current data defines so
  // stale keys from older data never leak in.
  const effectiveRowGroupsInitialState = useMemo(() => {
    if (!persistedRowGroups) {
      return rowGroupsInitialState;
    }
    const merged: RowsGroups = { ...rowGroupsInitialState };
    Object.keys(rowGroupsInitialState).forEach(alias => {
      const persistedState = persistedRowGroups[alias];
      if (persistedState) {
        merged[alias] = persistedState;
      }
    });
    return merged;
  }, [rowGroupsInitialState, persistedRowGroups]);

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, effectiveRowGroupsInitialState);

  // Read the live fold state inside the reset effect without listing it as a
  // dependency (which would re-fire the reset on every expand/collapse).
  const rowGroupsRef = useRef(rowGroups);
  rowGroupsRef.current = rowGroups;

  // Signature of the value persistence already holds, so the save effect can tell a
  // genuine user change from the state it just loaded. The flag lets the reset
  // effect suppress the save effect's stale same-commit run (where rowGroups has
  // not yet caught up to the reset payload) so cold mount does not write/broadcast.
  const lastPersistedSignatureRef = useRef<string | null>(null);
  const skipNextPersistRef = useRef(false);

  // Re-seed the reducer when the merged initial state changes — either because the
  // data shape changed (securities/share values) or because persisted overrides
  // hydrated from IndexedDB (async, ~100-300ms after mount). Aliases the user has
  // explicitly toggled (stored as USER_*) are preserved so an expand/collapse made
  // during the pre-hydration window is not silently overwritten by the older saved
  // preference; SYSTEM_* defaults are always replaced by the freshly merged state.
  useEffect(() => {
    const liveRowGroups = rowGroupsRef.current;
    const payload: RowsGroups = { ...effectiveRowGroupsInitialState };
    Object.keys(payload).forEach(alias => {
      const liveState = liveRowGroups[alias];
      if (liveState === USER_EXPAND || liveState === USER_COLLAPSE) {
        payload[alias] = liveState;
      }
    });
    // effectiveRowGroupsInitialState is exactly what persistence already holds
    // (merged onto defaults), so record it as already-saved and skip the save
    // effect's next run. That avoids a redundant cold-mount write and broadcast;
    // if the payload diverges because a pre-hydration toggle was preserved above,
    // the save effect still detects it (signature != seed) and persists it.
    lastPersistedSignatureRef.current = JSON.stringify(effectiveRowGroupsInitialState);
    skipNextPersistRef.current = true;
    setRowGroups({ type: RESET, payload });
  }, [effectiveRowGroupsInitialState]);

  // Save the live collapse state on change (skip during export — that render is
  // read-only and must not overwrite the user's saved preference). The signature
  // guard prevents a write/broadcast/RESET feedback loop when the value is
  // unchanged.
  useEffect(() => {
    if (isExportMode || !isRowGroupsHydrated || !rowGroupsStorageKey) {
      return;
    }
    // The reset effect just re-seeded the reducer; rowGroups is still the stale
    // pre-reset value on this commit, so swallow exactly one run to avoid writing
    // it back. The post-reset commit then runs the real signature check below.
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    const relevant: RowsGroups = {};
    Object.keys(rowGroupsInitialState).forEach(alias => {
      relevant[alias] = rowGroups[alias];
    });
    const signature = JSON.stringify(relevant);
    if (signature === lastPersistedSignatureRef.current) {
      return;
    }
    lastPersistedSignatureRef.current = signature;
    setPersistedRowGroups(relevant);
  }, [
    rowGroups,
    rowGroupsInitialState,
    isExportMode,
    isRowGroupsHydrated,
    rowGroupsStorageKey,
    setPersistedRowGroups,
  ]);

  useEffect(() => {
    if (!setSpreadsheetNotesInRangeSensitivity) {
      return;
    }
    setSpreadsheetNotesInRangeSensitivity(prevState => {
      const tmpEntries = prevState.filter(entry => entry.panelId !== approachPanelId);

      if (isEmpty(spreadsheetNotes)) {
        return tmpEntries;
      }

      return [
        ...tmpEntries,
        {
          notes: spreadsheetNotes,
          panelId: approachPanelId,
          hasChanged: spreadsheetNotesHasChanged,
        },
      ];
    });
  }, [spreadsheetNotes, spreadsheetNotesHasChanged, approachPanelId, setSpreadsheetNotesInRangeSensitivity]);

  useEffect(() => {
    if (!setFilesInRangeSensitivity) {
      return;
    }
    setFilesInRangeSensitivity(prevState => {
      const tmpFiles = prevState.filter(file => file.panelId !== approachPanelId);

      if (isEmpty(filesToSave)) {
        return tmpFiles;
      }

      return [
        ...tmpFiles,
        {
          filesToSave: filesToSave as ValuationFile['filesToSave'],
          panelId: approachPanelId,
        },
      ];
    });
  }, [filesToSave, approachPanelId, setFilesInRangeSensitivity]);

  const sheetProps = useMemo(() => {
    if (rangeSensitivitySheet) {
      return {
        ...rangeSensitivitySheet,
        onChange,
        rowGroups,
        setRowGroups,
        sheet: rangeSensitivitySheet,
        workbook,
        format: perShareFormat,
        formatDispatch,
        showToolbar: true,
      } as unknown as typeof ScalarSpreadsheet;
    }
    return null;
  }, [rangeSensitivitySheet, onChange, rowGroups, setRowGroups, workbook, perShareFormat, formatDispatch]);

  const pctSheetProps = useMemo(() => {
    if (sensitivityPctSheet) {
      return {
        ...sensitivityPctSheet,
        onChange,
        sheet: sensitivityPctSheet,
        workbook,
        showToolbar: false,
      } as unknown as typeof ScalarSpreadsheet;
    }
    return null;
  }, [sensitivityPctSheet, onChange, workbook]);

  // Hold the capture until every sheet's data is present and, in export mode, the
  // persisted collapse state has loaded — otherwise rows the user hid would be
  // captured while still expanded.
  const isRangeSensitivityExportLoading =
    !rangeSensitivitySheet?.data ||
    (!!sensitivityPctSheet && !sensitivityPctSheet.data) ||
    (isExportMode && !isRowGroupsHydrated);
  const rangeSensitivityExportStatus: 'loading' | 'ready' = isRangeSensitivityExportLoading
    ? 'loading'
    : 'ready';
  useExportSignal({
    target: PdfExportTargets.RANGE_SENSITIVITY.id,
    status: rangeSensitivityExportStatus,
    enabled: isExportMode,
  });

  return (
    <Grid container spacing={2}>
      <Grid
        data-ref={PdfExportTargets.RANGE_SENSITIVITY.id}
        size={{ xs: 12 }}
        style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <Grid container spacing={2}>
          <Grid data-testid="range-sensitivity-spreadsheet" size={{ xs: 12 }}>
            <CustomScalarSpreadsheet {...sheetProps} isLoading={isLoading} isSaving={isSaving} />
          </Grid>

          {pctSheetProps && (
            <Grid
              data-testid="range-sensitivity-pct-spreadsheet"
              size={{ xs: 12, sm: 5, md: 4 }}>
              <CustomScalarSpreadsheet {...pctSheetProps} isLoading={isLoading} isSaving={isSaving} />
            </Grid>
          )}
        </Grid>
      </Grid>

      <WidgetDrawer
        applicationContext={WidgetDrawerApplicationContext.RANGE_SENSITIVITY}
        notesProps={{
          pageType: RANGE_SENSITIVITY_PAGE_VALUE,
          pageTypeKey: RANGE_SENSITIVITY_PAGE_KEY,
          pageTypeId: spreadsheetNotesFeatureId,
          notes: cachedNotes,
          isApproach: true,
          setNotes,
          onAddNote,
          onUpdateNotes,
          onDeleteNote,
          isDisabled,
        }}
        documentReferencesProps={{
          selectedMeasurementDate: measurementDate,
          referenceType: RANGE_SENSITIVITY_REFERENCE_TYPE,
          currentPage: RANGE_SENSITIVITY_REFERENCE_TITLE,
          referencedFeatureId: featureId,
          filesToSave,
          setFilesToSave,
          isDisabled,
        }}
        spreadsheetNotesProps={{
          pageType: RANGE_SENSITIVITY_PAGE_VALUE,
          pageTypeKey: RANGE_SENSITIVITY_PAGE_KEY,
          pageTypeId: spreadsheetNotesFeatureId,
          notes: spreadsheetNotes,
          setNotes: setSpreadsheetNotes,
          handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          isApproach: true,
          isDisabled,
        }}
      />
    </Grid>
  );
};

export default RangeSensitivity;
